import { useServiceAccess, useServiceTypePlace, useTemplateEditor } from '@church/data'
import { formatDuration } from '@church/domain'
import { faClock, faListOl, faPen, faStar, faUsers } from '@church/ui-icons'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { RunningOrderCard } from '../components/services/RunningOrder.tsx'
import { SaveFailure } from '../components/services/SaveStatus.tsx'
import {
  TemplateDetailsPanel,
  TemplateNotesCard,
  TemplateTeamsCard,
  TemplateTimesCard,
} from '../components/services/Templates.tsx'
import {
  Chip,
  ErrorNote,
  Icon,
  IconButton,
  MOBILE_QUERY,
  PageHeader,
  pressable,
  ShownSection,
  Spinner,
  useEdgeFade,
  useMediaQuery,
} from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'
import { folderCrumbs } from '../lib/services-crumbs.ts'
import { useTabParam } from '../lib/tabs.ts'
import { switchSection } from '../lib/view-transition.ts'

/**
 * A plan template, edited like a plan (docs/14-TEAMS-PEOPLE-TEMPLATES.md):
 * its running order with the plan's own screens, its times (days around the
 * plan's), the people each position needs, and its details. Phones and
 * tablets show one section at a time behind pills; a computer shows them
 * all, the order on the right. Its Administrators change it; Editors read it.
 */
export const Route = createFileRoute(
  '/_authed/_shell/services/types/$typeId/templates/$templateId',
)({
  validateSearch: z.object({ tab: z.enum(['Order', 'Times', 'Teams']).optional() }),
  component: TemplateScreen,
})

const SECTIONS = ['Order', 'Times', 'Teams'] as const
const SECTION_ICONS = { Order: faListOl, Times: faClock, Teams: faUsers } as const

function TemplateScreen() {
  const { typeId, templateId } = Route.useParams()
  const navigate = useNavigate()
  const editor = useTemplateEditor(templateId)
  const access = useServiceAccess()
  const place = useServiceTypePlace(typeId)
  const mobile = useMediaQuery(MOBILE_QUERY)
  const [section, setSection] = useTabParam(SECTIONS)
  const pills = useEdgeFade()
  const [detailsOpen, setDetailsOpen] = useState(false)
  // Its Administrators came from the settings page (G3b): Plans › Main campus ›
  // Sunday Morning › Settings › Standard Sunday. Its Editors, from Templates.
  const admin = access.can(typeId, 'administrator')
  useBreadcrumbs([
    ...folderCrumbs(place.folders),
    ...(place.type ? [{ label: place.type.name, href: `/services/types/${typeId}` }] : []),
    admin
      ? { label: 'Settings', href: `/services/types/${typeId}/settings?tab=Templates` }
      : { label: 'Templates', href: `/services/types/${typeId}/templates` },
    { label: editor.template?.name ?? 'Template' },
  ])

  if (editor.isLoading) return <Spinner />
  if (!editor.template) return <ErrorNote error={editor.error} />
  const template = editor.template
  const editable = access.can(template.serviceTypeId, 'administrator')
  const order = { kind: 'template', id: template.id } as const
  const orderCard = <RunningOrderCard order={order} editable={editable} times={template.times} />

  return (
    <div className="space-y-6">
      <PageHeader
        title={template.name}
        facts={
          <span className="inline-flex flex-wrap items-center gap-2">
            {template.isDefault && (
              <Chip tone="brand">
                <Icon icon={faStar} className="size-3" />
                Default
              </Chip>
            )}
            <span className="tabular-nums">
              {[
                template.totalSeconds > 0 && `${formatDuration(template.totalSeconds)} long`,
                `${template.times.length} ${template.times.length === 1 ? 'time' : 'times'}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
        }
        action={
          editable ? (
            <IconButton
              icon={faPen}
              label="Edit template details"
              onClick={() => setDetailsOpen(true)}
            />
          ) : undefined
        }
      />
      <SaveFailure order={order} />
      {editable && (
        <TemplateDetailsPanel
          key={template.id}
          template={template}
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          onDeleted={() =>
            void navigate({
              to: '/services/types/$typeId/settings',
              params: { typeId },
              search: { tab: 'Templates' },
            })
          }
        />
      )}
      {mobile && (
        <nav
          aria-label="Template sections"
          className="sticky under-bar z-3 -mx-gutter -mt-4 mb-2! bg-surface-base"
        >
          <div
            ref={pills.ref}
            className={`tabs-fade flex gap-2 overflow-x-auto px-3 py-2 no-scrollbar ${pills.edge}`}
          >
            {SECTIONS.map((name) => (
              <button
                key={name}
                type="button"
                aria-pressed={section === name}
                onClick={() => switchSection(() => setSection(name))}
                className={`${pressable} inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-pill leading-none font-semibold transition-colors duration-(--duration-base) ${
                  section === name
                    ? 'bg-brand-solid text-brand-on-solid'
                    : 'bg-surface-heading text-content-default hover:bg-surface-section'
                }`}
              >
                <Icon icon={SECTION_ICONS[name]} className="size-3.5" />
                {name}
              </button>
            ))}
          </div>
        </nav>
      )}
      {mobile ? (
        <ShownSection value={section}>
          <div className="plan-sections space-y-6">
            {section === 'Order' && (
              <>
                {orderCard}
                <TemplateNotesCard template={template} />
              </>
            )}
            {section === 'Times' && <TemplateTimesCard template={template} editable={editable} />}
            {section === 'Teams' && <TemplateTeamsCard template={template} editable={editable} />}
          </div>
        </ShownSection>
      ) : (
        <div className="grid grid-cols-plan items-start gap-6">
          <div className="min-w-0 space-y-6">
            <TemplateTimesCard template={template} editable={editable} />
            <TemplateNotesCard template={template} />
            <TemplateTeamsCard template={template} editable={editable} />
          </div>
          <div className="min-w-0">{orderCard}</div>
        </div>
      )}
    </div>
  )
}
